// 工作項目(work_item)共用常數 — type / role / status
// 依全域規則:enum + label 集中一處,zod/UI/API 都從這裡取,避免 drift。

export const WORK_ITEM_TYPES = ['feature', 'bug', 'task'];
export const WORK_ITEM_TYPE_LABELS = {
  feature: 'DesignChange',
  bug: 'Issue',
  task: 'Task',
  improvement: 'Improve', // 已從選單移除，保留 label 作為既有資料 fallback
};
export const WORK_ITEM_TYPE_COLORS = {
  feature: { bg: '#dbeafe', fg: '#2563eb' },
  bug: { bg: '#fef3f2', fg: '#b42318' },
  task: { bg: '#f2f4f7', fg: '#475467' },
  improvement: { bg: '#ecfdff', fg: '#0e7090' },
};

// 角色(function list 角色欄)
export const WORK_ITEM_ROLES = ['FE', 'BE', 'QA', 'Design', 'PM'];
export const WORK_ITEM_ROLE_COLORS = {
  FE: { bg: '#ecfdff', fg: '#0e7090' },
  BE: { bg: '#f4f3ff', fg: '#6938ef' },
  QA: { bg: '#fffaeb', fg: '#b54708' },
  Design: { bg: '#fdf2fa', fg: '#c11574' },
  PM: { bg: '#f4f3ff', fg: '#5925dc' },
};

// 狀態:權威配色仍以 StatusBadge.jsx 為主,這裡只列可選值與預設
export const WORK_ITEM_STATUSES = ['open', 'in_progress', 'verify', 'completed', 'blocked', 'tbd', 'closed'];
export const WORK_ITEM_STATUS_LABELS = {
  open: '待處理',
  in_progress: '進行中',
  verify: '待驗證',
  completed: '已完成',
  blocked: '受阻',
  tbd: '待定',
  closed: '已關閉',
};
export const DEFAULT_WORK_ITEM_STATUS = 'open';

// 狀態色票(pill:bg/文字 fg/圓點 dot)
export const WORK_ITEM_STATUS_COLORS = {
  open: { bg: '#f2f4f7', fg: '#475467', dot: '#98a2b3' },
  in_progress: { bg: '#eff8ff', fg: '#1570ef', dot: '#1570ef' },
  verify: { bg: '#fffaeb', fg: '#b54708', dot: '#f79009' },
  completed: { bg: '#ecfdf3', fg: '#067647', dot: '#12b76a' },
  blocked: { bg: '#fef3f2', fg: '#b42318', dot: '#f04438' },
  tbd: { bg: '#f2f4f7', fg: '#475467', dot: '#98a2b3' },
  closed: { bg: '#f2f4f7', fg: '#98a2b3', dot: '#d0d5dd' },
};

// 交付紀錄:標籤 + 環境(WorkItemDetailDrawer 詳情 / 批次操作 共用)
export const DELIVERY_LABELS = ['Frontend', 'Backend', 'QA', 'Design', 'PM'];
export const DELIVERY_LABEL_COLORS = { Frontend: '#667eea', Backend: '#38a169', QA: '#ed8936', Design: '#ed64a6', PM: '#9f7aea' };
export const ENV_OPTIONS = [
  { value: 'staging', label: 'Staging' }, { value: 'production', label: 'Production' }, { value: 'dev', label: 'Dev' },
];
export const ENV_LABELS = { staging: 'Staging', production: 'Production', dev: 'Dev' };

// 優先度(對齊舊 TicketDetailPage PRIORITY_OPTIONS:實心色 pill)
export const WORK_ITEM_PRIORITY_META = {
  0: { label: '無', color: '#94a3b8' },
  1: { label: '低', color: '#94a3b8' },
  2: { label: '中', color: '#f59e0b' },
  3: { label: '高', color: '#ef4444' },
  4: { label: '緊急', color: '#dc2626' },
};

/**
 * 搜尋字串是不是「只查編號」的指示。
 *
 * 規則跟舊 Ticket 一致(tickets.js:395):**# 是必要的**。沒帶 # 的純數字要走文字
 * 搜尋 —— 標題/說明裡含數字的項目(「修 39 頁的排版」)打 39 必須找得到,否則反直覺。
 * 工作項目一度寫成 /^#?\d+$/(# 可選),純數字就被編號查詢吃掉,是搬 Ticket 時搬錯的。
 *
 * 這條規則同時給伺服器端(searchWorkItems)、專案內即時篩選、我的工作項目三處用,
 * 放在這裡是為了不要再各自寫一份然後慢慢漂移(原本那三處真的是三種寫法)。
 */
export const isWorkItemNoSearch = (term) => /^#\d+$/.test((term || '').trim());

/** 從 #編號 搜尋字串取出編號;不是編號搜尋則回 null。 */
export const parseWorkItemNoSearch = (term) => (isWorkItemNoSearch(term) ? Number(String(term).trim().slice(1)) : null);

/**
 * 同層子項的排序。
 *
 * 子項沒有 work_item_no(migration 259 只發給頂層),display_order 也從來沒被寫過
 * (updateWorkItemOrder 全專案沒人呼叫),所以兩個排序鍵完全打平 —— Postgres 不保證
 * 順序,改過的子項會因為 heap 位置變動而跳到最後面。這裡補一個穩定且可預期的依據。
 *
 * 排序依據依序是:
 *   1. display_order —— 使用者拖曳出來的順序(全域,所有人看到一樣)
 *   2. 標題 —— 沒拖過的一律是 0,靠標題給一個可預期的預設順序
 *   3. id —— 最終 tiebreaker,確保完全確定性
 *
 * 標題用 localeCompare 而不是丟給資料庫排:DB collation 對中文是按 UTF-8 byte 排,
 * 看起來像亂序;localeCompare 走 zh-Hant,並開 numeric 讓「子項 2」排在「子項 10」前面。
 */
export const compareWorkItemSiblings = (a, b) => {
  const o = Number(a?.display_order ?? 0) - Number(b?.display_order ?? 0);
  if (o !== 0) return o;
  const t = String(a?.title ?? '').localeCompare(String(b?.title ?? ''), 'zh-Hant', { numeric: true, sensitivity: 'base' });
  if (t !== 0) return t;
  return Number(a?.id ?? 0) - Number(b?.id ?? 0);
};

/**
 * 把「可見子項的新順序」合併回「完整子項清單」。
 *
 * 拖曳時畫面上可能只顯示部分子項(closed 被濾掉、有狀態/角色篩選、
 * 在「我的工作項目」只看得到指派給自己的)。只把可見的順序寫回去,被藏起來的會
 * 保留舊 display_order,篩選一拿掉順序就錯亂。
 *
 * 作法是「位置保留」:被藏起來的子項釘在它原本的位置不動,可見的依新順序依序填入
 * 剩下的位置。這樣不管畫面上篩掉了什麼,拖曳都只會影響你看得到的那幾個。
 *
 * @param {Array<{id:number}>} fullList 該母項的完整子項(依目前排序)
 * @param {Array<number>} visibleOrderedIds 可見子項拖曳後的新順序
 * @returns {Array<number>} 完整的 id 順序,可直接餵給 updateWorkItemOrder
 */
export const mergeSiblingOrder = (fullList, visibleOrderedIds) => {
  const queue = [...(visibleOrderedIds || [])];
  const visible = new Set(queue);
  const merged = (fullList || []).map((c) => (visible.has(c.id) ? queue.shift() : c.id));
  // fullList 沒涵蓋到的(理論上不該發生)接在後面,不要靜默掉資料
  return [...merged, ...queue];
};
